import { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, Info, Lightbulb, Sparkles, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatMonthLabel } from '@/lib/months';
import { formatCurrency } from '@/lib/formatters';
import { checkAlarm } from '@/features/projections/engine';
import type { ProjectionModel } from '@/features/projections/useProjectionModel';
import { buildSnapshot, localInsights } from '@/features/projections/insights';
import {
  useGenerateInsights,
  useLatestInsights,
  type Insight,
} from '@/features/projections/workbookHooks';

interface Props {
  pm: ProjectionModel;
  scenarioName: string;
  onJump?: (rowKey: string | null | undefined, month: string | null | undefined) => void;
}

const SEVERITY: Record<
  Insight['severity'],
  { label: string; icon: typeof Info; className: string; order: number }
> = {
  critical: {
    label: 'Critical',
    icon: XCircle,
    className: 'border-red-200 bg-red-50 text-red-900',
    order: 0,
  },
  warning: {
    label: 'Watch',
    icon: AlertTriangle,
    className: 'border-amber-200 bg-amber-50 text-amber-900',
    order: 1,
  },
  opportunity: {
    label: 'Opportunity',
    icon: Lightbulb,
    className: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    order: 2,
  },
  info: { label: 'Note', icon: Info, className: 'border-border bg-card text-foreground', order: 3 },
};

/**
 * Insights & checks: rule-based flags (always on), the three integrity
 * checks across the visible range, and an on-demand Claude analysis that
 * is persisted so the exec team sees the same narrative.
 */
export function InsightsTab({ pm, scenarioName, onJump }: Props) {
  const latest = useLatestInsights(pm.scenario);
  const generate = useGenerateInsights(pm.scenario);

  const rows = useMemo(() => pm.buildRows('main'), [pm]);
  const flags = useMemo(
    () => (pm.model ? localInsights(pm.model, rows, pm.visibleMonths, pm.settings) : []),
    [pm.model, rows, pm.visibleMonths, pm.settings],
  );

  if (pm.isLoading || !pm.model) return <Skeleton className="h-[480px] w-full" />;
  const model = pm.model;

  const aiInsights = parseInsights(latest.data?.insights);
  const sorted = (list: Insight[]) =>
    [...list].sort((a, b) => SEVERITY[a.severity].order - SEVERITY[b.severity].order);

  const runAi = () => {
    const snapshot = buildSnapshot(model, rows, pm.visibleMonths, pm.settings, scenarioName);
    generate.mutate(
      { snapshot },
      {
        onSuccess: (r) => toast.success(`Analysis ready — ${r.insights.length} findings`),
        onError: (e) => toast.error(e instanceof Error ? e.message : 'AI analysis failed'),
      },
    );
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> AI analysis
              </CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                Reads the visible range of this scenario — plan vs. actual, cash chain, sales
                drivers — and writes up what the exec team should look at. Nothing is changed in the
                workbook.
              </p>
            </div>
            <Button size="sm" onClick={runAi} disabled={generate.isPending}>
              <Sparkles className="h-3.5 w-3.5" />
              {generate.isPending ? 'Analysing…' : latest.data ? 'Re-run' : 'Analyse'}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {latest.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : latest.data ? (
              <>
                <p className="text-sm leading-relaxed">{latest.data.summary}</p>
                <p className="text-[11px] text-muted-foreground">
                  {latest.data.model} · {new Date(latest.data.created_at).toLocaleString()}
                </p>
                <InsightList items={sorted(aiInsights)} onJump={onJump} />
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                No analysis yet for this scenario. Click Analyse to generate one.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Automatic flags</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Recomputed live from the same engine that paints the grid, using the thresholds in
              Settings.
            </p>
          </CardHeader>
          <CardContent>
            {flags.length === 0 ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Nothing alarming in the
                visible range.
              </p>
            ) : (
              <InsightList items={sorted(flags)} onJump={onJump} />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Integrity checks</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Three independent recomputations of the model. All three must read zero for every
              month — this is the “triple check”.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {model.checks.map((c) => {
              const idx = pm.visibleMonths
                .map((m) => model.months.indexOf(m))
                .filter((i) => i >= 0);
              const failing = idx.filter((i) => checkAlarm(c.diffs[i], c.tolerance) === 'bad');
              const checked = idx.filter((i) => c.diffs[i] !== null).length;
              return (
                <div key={c.key} className="rounded-md border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">{c.label}</span>
                    {failing.length === 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" /> {checked} month
                        {checked === 1 ? '' : 's'} OK
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-red-700">
                        <XCircle className="h-3.5 w-3.5" /> {failing.length} failing
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{c.description}</p>
                  {failing.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-xs">
                      {failing.slice(0, 6).map((i) => (
                        <li key={i} className="flex justify-between">
                          <span>{formatMonthLabel(model.months[i])}</span>
                          <span className="tabular-nums text-red-700">
                            {formatCurrency(c.diffs[i] ?? 0)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>How the numbers connect</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs text-muted-foreground">
            <p>
              <strong className="text-foreground">Sales drivers</strong> → units × tier price =
              revenue; units × unit COGS = cost of goods. These lines are read-only in the P&amp;L.
            </p>
            <p>
              <strong className="text-foreground">P&amp;L</strong> → every other revenue / expense
              account is a plan you type. Actuals arrive from QuickBooks after month end.
            </p>
            <p>
              <strong className="text-foreground">Cash section</strong> → non-P&amp;L movements
              (inventory, AR, AP, credit cards, loans, owner draws, capital items) as cash in / out.
            </p>
            <p>
              <strong className="text-foreground">Ending bank balance</strong> = opening bank + net
              income + cash-section movements. Actual = QuickBooks bank accounts at month end, which
              also re-anchors every later month's plan.
            </p>
            <p>
              <strong className="text-foreground">Balance sheet</strong> is derived from the same
              movements, so it balances by construction — the checks prove it.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function InsightList({ items, onJump }: { items: Insight[]; onJump?: Props['onJump'] }) {
  if (items.length === 0) return null;
  return (
    <ul className="space-y-2">
      {items.map((it, i) => {
        const s = SEVERITY[it.severity] ?? SEVERITY.info;
        const Icon = s.icon;
        return (
          <li key={`${it.title}-${i}`} className={`rounded-md border p-3 ${s.className}`}>
            <div className="flex items-start gap-2">
              <Icon className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center justify-between gap-x-2">
                  <p className="text-sm font-medium">{it.title}</p>
                  <span className="text-[10px] uppercase tracking-wide opacity-70">{s.label}</span>
                </div>
                <p className="mt-1 text-xs leading-relaxed opacity-90">{it.detail}</p>
                {it.suggestion && (
                  <p className="mt-1.5 text-xs leading-relaxed">
                    <span className="font-medium">Suggestion:</span> {it.suggestion}
                  </p>
                )}
                {(it.row_key || it.month) && onJump && (
                  <button
                    type="button"
                    className="mt-1.5 text-[11px] underline-offset-2 hover:underline"
                    onClick={() => onJump(it.row_key, it.month)}
                  >
                    Open on Main sheet{it.month ? ` (${formatMonthLabel(it.month)})` : ''}
                  </button>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function parseInsights(raw: unknown): Insight[] {
  if (!Array.isArray(raw)) return [];
  const out: Insight[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    const severity = o.severity;
    if (typeof o.title !== 'string' || typeof o.detail !== 'string') continue;
    out.push({
      severity:
        severity === 'critical' || severity === 'warning' || severity === 'opportunity'
          ? severity
          : 'info',
      title: o.title,
      detail: o.detail,
      suggestion: typeof o.suggestion === 'string' ? o.suggestion : null,
      row_key: typeof o.row_key === 'string' ? o.row_key : null,
      month: typeof o.month === 'string' ? o.month : null,
    });
  }
  return out;
}
